import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../contexts/LanguageContext";

// 26 supported locales — must match translations dictionaries
const ALL_LOCALES = [
  "en", "sk", "cs", "de", "pl", "hu", "fr", "es", "pt", "it", "nl", "sv",
  "no", "da", "fi", "ro", "bg", "hr", "uk", "tr", "ja", "ko", "zh", "hi",
  "ar", "id",
];

/**
 * Injects per-page SEO tags:
 * - <title>, <meta name="description">
 * - Open Graph (og:title / og:description / og:locale)
 * - Twitter Card
 * - Canonical URL
 * - hreflang alternates for all supported locales
 * - JSON-LD WebPage schema
 *
 * @param {string} slug      Canonical slug (e.g. "ai-visibility")
 * @param {string} title     Localized <title>
 * @param {string} description Localized meta description
 * @param {string} [type]    Schema.org type (default WebPage)
 */
export default function SEOHead({ slug, title, description, type = "WebPage", image }) {
  const { language } = useLanguage();
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://toprankpulse.com";

  // Canonical URL — for the root (no slug) we canonicalise to the bare
  // origin so `/` and `/en` don't compete for the same ranking. For
  // localised landing pages we still emit `/{lang}/{slug}`.
  const canonicalPath = slug ? `/${language}/${slug}` : "/";
  const canonical = `${origin}${canonicalPath}`;
  const ogImage = image || "https://toprankpulse.com/og-image.png";

  // Ensure document.lang attribute updates (for accessibility + crawler hints)
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": type,
    name: title,
    description,
    url: canonical,
    inLanguage: language,
    publisher: {
      "@type": "Organization",
      name: "TopRankPulse",
      url: origin,
    },
  };

  return (
    <Helmet>
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Canonical */}
      <link rel="canonical" href={canonical} />

      {/* hreflang alternates — one per supported locale + x-default */}
      {ALL_LOCALES.map((loc) => (
        <link
          key={loc}
          rel="alternate"
          hrefLang={loc}
          href={`${origin}/${loc}${slug ? `/${slug}` : ""}`}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${origin}${slug ? `/en/${slug}` : "/"}`}
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={language} />
      <meta property="og:site_name" content="TopRankPulse" />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}

export { ALL_LOCALES };
